import type { EntityTable, IDType } from "dexie";
import { batch } from "solid-js";
import { createStore, produce, reconcile } from "solid-js/store";
import type { DbEntities } from "./createSyncService";

export type EntitySyncManager<T extends DbEntities[keyof DbEntities]> = ReturnType<typeof createEntitySyncManager<T>>;

export const createEntitySyncManager = <Entity extends DbEntities[keyof DbEntities]>(
  db: EntityTable<Entity, "id"> | undefined,
  getFuzzyText?: (entity: Entity) => string[],
) => {
  type Id = Entity["id"];
  type DbId = IDType<Entity, "id">;
  type EntityStore = Record<Id, Entity>;

  const [data, setData] = createStore<{ ids: Id[]; byId: EntityStore }>({
    ids: [],
    byId: {} as EntityStore,
  });

  // TODO: @andi, add smarter caching of the fuzzy preprocessing
  // For now it's better to pay the cost at search time rather than preprocess every
  // single time a record changes
  const searchIds = (query: string): Id[] => {
    if (!getFuzzyText) return data.ids;
    if (!query) return data.ids;

    // const fuzzy = createFuzzySearch(Object.values(data.byId), {
    //   getText: getFuzzyText,
    // });
    const values = Object.values(data.byId) as Entity[];

    // return fuzzy(query).map((result) => (result.item as Entity).id) as Id[];
    return values
      .filter((d) =>
        getFuzzyText(d).some((t) =>
          t.replaceAll(" ", "").toLowerCase().includes(query.replaceAll(" ", "").toLowerCase()),
        ),
      )
      .map((d) => d.id) as Id[];
  };
  const search = (query: string): Entity[] => {
    if (!query) return Object.values(data.byId);
    return searchIds(query)
      .map((id) => data.byId[id])
      .filter(Boolean);
  };

  const setNoDb = (id: Id, record: Entity) => {
    batch(() => {
      setData("ids", reconcile(Array.from(new Set([...data.ids, id]))));
      // biome-ignore lint/suspicious/noExplicitAny: The generics are too complex for ts to infer
      setData("byId", id as any, reconcile(record as any));
    });
  };

  const set = (id: Id, record: Entity) => {
    setNoDb(id, record);
    db?.put(record);
  };

  const remove = (id: Id) => {
    setData(
      produce((store) => {
        store.ids = store.ids.filter((i) => i !== id);
        delete store.byId[id];
      }),
    );
    db?.delete(id as DbId);
  };

  const get = (id: Id): Entity | undefined => data.byId[id];

  const clear = () => {
    setData({ ids: [], byId: {} as EntityStore });
    db?.clear();
  };

  const isDeleted = (id: Id) => {
    const record = get(id);
    return record && "deletedAt" in record;
  };
  const isArchived = (id: Id) => {
    const record = get(id);
    return record && "isArchived" in record && record.isArchived;
  };

  const setBulk = (records: Entity[]) => {
    db?.bulkPut(records);

    batch(() => {
      for (const record of records) {
        setNoDb(record.id, record);
      }
    });
  };

  const removeBulk = (ids: Id[]) => {
    db?.bulkDelete(ids as DbId[]);
    setData(
      produce((store) => {
        store.ids = store.ids.filter((id) => !ids.includes(id));
        for (const id of ids) {
          delete store.byId[id];
        }
      }),
    );
  };

  const hydrateFromDb = async (userId: string) => {
    const records = await db?.toArray();
    const toAdd: Entity[] = [];
    const toRemove: Id[] = [];

    for (const record of records || []) {
      if (record.userId === userId) {
        toAdd.push(record);
      } else {
        toRemove.push(record.id);
      }
    }

    batch(() => {
      clear();
      setBulk(toAdd);
      removeBulk(toRemove);
    });
  };

  const oldValuesCache = new Map<Id, Entity>();

  const hydrateFromSync = async (records: Entity[], holdOldValues = false) => {
    const ids: Id[] = [];
    const byId = {} as EntityStore;
    for (const record of records) {
      ids.push(record.id);
      byId[record.id as Id] = record;
      db?.put(record);
    }
    const newIds = new Set(ids);
    const oldIds = new Set(data.ids);

    for (const id of oldIds.difference(newIds)) {
      if (holdOldValues) {
        oldValuesCache.set(id, data.byId[id]);
      }
      db?.delete(id as DbId);
    }

    setData(reconcile({ ids, byId }, { merge: true }));
  };

  return {
    _rawData: data,
    oldValuesCache,
    set,
    remove,
    clear,
    get,
    isDeleted,
    isArchived,
    hydrateFromDb,
    hydrateFromSync,
    removeBulk,
    searchIds,
    search,
  };
};
